import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { LOADER_BARS } from './loader-markup';

/**
 * État « envoi en cours » d'un bouton : `<button [appLoading]="isSaving()">`.
 *
 * Le libellé reste dans le bouton, masqué, et le loader se pose par-dessus : le bouton garde
 * sa largeur. Remplacer le libellé par un spinner faisait rétrécir le bouton au clic, et
 * rompait l'égalité de largeur de la paire Annuler/Confirmer.
 *
 * Le bouton est désactivé tant que `appLoading` est vrai, ce qui empêche le double envoi.
 * Un `[disabled]` posé à côté est intercepté ici et combiné : le passer normalement.
 *
 * Le loader prend `currentColor` : lisible sur un bouton plein comme sur un bouton contour.
 */
@Component({
  // Sélecteur d'attribut assumé : le composant doit ÊTRE le <button> natif (focus, submit,
  // disabled) et non l'envelopper — même choix que `button[mat-button]` chez Material.
  selector: 'button[appLoading]',
  template: `
    <span class="loading-button__label"><ng-content /></span>
    @if (appLoading()) {
      <span class="moon-loader moon-loader--xs loading-button__loader" aria-hidden="true">
        @for (bar of bars; track bar) {
          <span class="moon-loader__bar"></span>
        }
      </span>
    }
  `,
  styles: [
    `
      /* display: contents — le libellé n'ajoute aucune boîte : le flex, le gap et les marges
         du bouton s'appliquent à son contenu exactement comme avant. */
      .loading-button__label {
        display: contents;
      }

      :host(.is-loading) {
        position: relative;
      }

      :host(.is-loading) .loading-button__label {
        visibility: hidden;
      }

      .loading-button__loader {
        --moon-loader-color: currentColor;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    `,
  ],
  host: {
    '[disabled]': 'disabled() || appLoading()',
    '[attr.aria-busy]': 'appLoading() || null',
    '[class.is-loading]': 'appLoading()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingButton {
  readonly appLoading = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly bars = LOADER_BARS;
}
